import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TrackingInput = ({ onTrack, isLoading, error }) => {
  const [inputValue, setInputValue] = useState('');
  const [validationError, setValidationError] = useState('');

  const handleInputChange = (e) => {
    const value = e?.target?.value?.toUpperCase();
    setInputValue(value);
    setValidationError('');
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (!inputValue?.trim()) {
      setValidationError('Please enter a tracking number');
      return;
    }

    if (inputValue?.length < 8) {
      setValidationError('Tracking number must be at least 8 characters');
      return;
    }

    onTrack(inputValue?.trim());
  };

  const handleClear = () => {
    setInputValue('');
    setValidationError('');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      className="bg-card border border-border rounded-lg shadow-warm p-6"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="tracking-number" className="block text-sm font-body font-medium text-foreground mb-2">
            Tracking Number
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Icon name="Package" size={20} className="text-muted-foreground" />
            </div>
            <input
              id="tracking-number"
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              placeholder="Enter tracking number (e.g., DWS123456789)"
              className="w-full pl-12 pr-12 py-3 text-base font-body bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-200"
              disabled={isLoading}
            />
            {inputValue && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-muted-foreground hover:text-foreground transition-colors duration-200"
                disabled={isLoading}
              >
                <Icon name="X" size={18} />
              </button>
            )}
          </div>
          {(validationError || error) && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2 text-sm font-body text-red-600 flex items-center gap-2"
            >
              <Icon name="AlertCircle" size={16} />
              {validationError || error}
            </motion.p>
          )}
        </div>

        <Button
          type="submit"
          disabled={isLoading || !inputValue?.trim()}
          className="w-full sm:w-auto px-8 py-3 bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              >
                <Icon name="Loader" size={20} />
              </motion.div>
              <span>Tracking...</span>
            </>
          ) : (
            <>
              <Icon name="Search" size={20} />
              <span>Track Shipment</span>
            </>
          )}
        </Button>
      </form>
      {/* Quick Tips */}
      <div className="mt-6 pt-6 border-t border-border">
        <p className="text-sm font-body font-medium text-foreground mb-3">Quick Tips:</p>
        <ul className="space-y-2 text-sm font-body text-muted-foreground">
          <li className="flex items-start gap-2">
            <Icon name="CheckCircle2" size={16} className="text-primary mt-0.5 flex-shrink-0" />
            <span>Tracking numbers are typically 8-15 characters long</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="CheckCircle2" size={16} className="text-primary mt-0.5 flex-shrink-0" />
            <span>Check your shipping confirmation email for your tracking number</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="CheckCircle2" size={16} className="text-primary mt-0.5 flex-shrink-0" />
            <span>Tracking information updates every few hours</span>
          </li>
        </ul>
      </div>
    </motion.div>
  );
};

export default TrackingInput;